import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetRedux } from 'redux/actions'
import { useMutation } from '@apollo/client';
import { client } from 'graphql/apolloClient';
import { LOGIN } from 'graphql/service';
import jwt_decode from "jwt-decode"
import Cookies from 'js-cookie';
import { debouncedErrorMessage } from 'utils/messageUtil';
import { message } from 'antd';

/**
 * A custom hook for auth includes:
 * - authProvide
 * - useAuthContext
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [validToken, setValidToken] = useState(Cookies.get('token') || '');

  useEffect(() => {
    const verifyAuth = () => {
      const token = Cookies.get('token');
      
      if (!token && validToken) {
        setValidToken('');
        Object.keys(Cookies.get()).forEach(Cookies.remove);
        localStorage.clear();
        client.clearStore();
        setValidToken('');
        dispatch(resetRedux());
        navigate('/login');
      }
    };

    // Run once immediately on mount
    verifyAuth();

    // 1. Check when they switch back to this tab
    window.addEventListener('focus', verifyAuth);

    // 2. Check every 30 seconds
    const interval = setInterval(verifyAuth, 30000);

    return () => {
      window.removeEventListener('focus', verifyAuth);
      clearInterval(interval);
    };
  }, [validToken]);

  const [login, { loading: loginLoading, error: loginError }]  = useMutation(
    LOGIN,
    { 
      onCompleted: (loginData) => {
        if(loginData) {
          const token = loginData.authenticate?.accessToken        
          const decoded = jwt_decode(token)
          if(decoded.user_type === 'TENANT') {
            const content = 'Unauthorized User Type!'
            debouncedErrorMessage(content);
            userLogout()
          } else {
            Cookies.set('userId', decoded.sub, { expires: new Date(decoded.exp * 1000) });
            Cookies.set('token', token, { expires: new Date(decoded.exp * 1000) });
            Cookies.set('fullName', decoded.username, { expires: new Date(decoded.exp * 1000)});
            setValidToken(token)
            navigate(localStorage.getItem('previousPath') || '/')
          }
        }
      },
      onError: (error) => {
        if (!error.networkError) {
          let content = error.message
          if(error.message.includes('Validation')) {
              content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
          }
          debouncedErrorMessage(content);
        }
      }
    }
  )

  const userLogout = useCallback(() => {
    Object.keys(Cookies.get()).forEach(Cookies.remove);
    localStorage.clear();
    client.clearStore();
    setValidToken('');
    dispatch(resetRedux());
    message.success('Log out successfully!');
    navigate('/login');
  }, [dispatch, navigate]);

  const userLogin = useCallback((email, password, setPasswordError) => {
    login({
      variables: {
        input: {
          login: email,
          password: password
        }
      }
    })

    if (loginError) {
      setPasswordError(true)
    }
  }, [login, loginError]);

  const memoedValue = useMemo(() => ({
    validToken,
    userLogin,
    loginLoading,
    userLogout
  }), [validToken, userLogin, loginLoading, userLogout]);

  return (
    <AuthContext.Provider value={memoedValue}>
      {children}
    </AuthContext.Provider>
  )
}


const useAuth = () => {
  return useContext(AuthContext);
}  

export default useAuth
